import { useBreakpoint } from '@chakra-ui/media-query';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { FeatureFlag, LOGIN, Loading } from '@revelio/core';

import packageJson from '../../package.json';
import { environment } from '../environments/environment';
import { useBrandMeta } from '../shared/branding/brand-provider';
import { useTopNOverrideFlagGuard } from '../shared/data-fetch/use-top-n-override-flag-guard';
import { DataInspectorAccessSync } from '../shared/data-inspector/data-inspector-access-sync';
import { DataInspectorDrawer } from '../shared/data-inspector/data-inspector-drawer';
import { DataInspectorIndicator } from '../shared/data-inspector/data-inspector-indicator';
import { DataInspectorNavigationSync } from '../shared/data-inspector/data-inspector-navigation-sync';
import { useHideNav } from '../shared/hooks/useHideNav';
import {
  useUnleashFlag,
  useUnleashVersionRefresh,
} from '../shared/unleash-flags/use-unleash-flag';
import '../styles/main.css';
// Global date-picker theme: it has to load with the app, not with a lazy page.
import '../styles/react-datepicker.css';

import { PrivateRoute } from './Auth/private-route';
import { useSessionTokenHandoff } from './Auth/use-session-token-handoff';
import { ImpersonationBanner } from './adminRewrite/impersonation/banner/ImpersonationBanner';
import { ImpersonationModal } from './adminRewrite/impersonation/settings/ImpersonationModal';
import { useFlaggedEntityAccess } from './CustomDash/CustomDashRoute';
import { PageManagerProvider } from './CustomDash/components/PageManagerProvider/PageManagerProvider';
import { useRecordDocumentArrival } from './CustomDash/components/PageState/hooks/document-arrival';
import { SharedPlotViewer } from './CustomDash/components/SharedReport/SharedPlotViewer';
import { SharedReportViewer } from './CustomDash/components/SharedReport/SharedReportViewer';
import { GlobalAIAssistantProvider } from './global-ai-assistant/global-ai-assistant-context';
import { GlobalAIAssistantHost } from './global-ai-assistant/global-ai-assistant-host';
import { useGlobalAIAssistantFeatureEnabled } from './global-ai-assistant/use-global-ai-assistant-feature-enabled';
import { Homepage } from './homepage/Homepage';
import Login from './login/login';
import { MobileUnsupportedPage } from './MobileUnsupportedPage';
import './next.module.css';

import type { NavItemConfig, RouteNavConfig } from '@revelio/auth';
import { useDisclosure } from '@revelio/ui';

import {
  LazyRoute,
  lazyNamedWithReload,
  lazyWithReload,
} from './routes/lazy-route';
import { RequireAuth } from './routes/ProtectedRoute';

const GameV2 = lazyWithReload(() => import('./gameV2/GameV2'));
const EconomicImpactPage = lazyNamedWithReload(
  () => import('./economic-impact/EconomicImpactPage'),
  'EconomicImpactPage'
);

import styles from './next.layout.module.css';
import { reducedRoutes } from './routes/reduced-routes';
import { navItemsNewLayout as allNavItems } from './routes/routes';
import SideNav from './routes/side-nav/side-nav';
import Tour from './tour/tour';

const ECONOMIC_IMPACT_PATHS = new Set<string>([
  'economic-impact',
  '/economic-impact',
]);

/**
 * Remove nav entries whose `path` is in `paths`, recursively. Used to hide
 * flag-gated surfaces from the side nav; the route tree is unaffected (each
 * gated route does its own flag-off redirect), so this only hides links.
 */
const filterPathsFromNav = (
  items: (RouteNavConfig | NavItemConfig)[],
  paths: ReadonlySet<string>
): (RouteNavConfig | NavItemConfig)[] =>
  items.reduce<(RouteNavConfig | NavItemConfig)[]>((acc, item) => {
    if (item.path && paths.has(item.path)) {
      return acc;
    }
    const navItem = item as RouteNavConfig;
    if (navItem.children) {
      acc.push({
        ...navItem,
        children: filterPathsFromNav(navItem.children, paths),
      });
      return acc;
    }
    acc.push(item);
    return acc;
  }, []);

/** Applied only when the economic-impact-new-layout-app flag is off. */
const filterEconomicImpactFromNav = (
  items: (RouteNavConfig | NavItemConfig)[]
): (RouteNavConfig | NavItemConfig)[] =>
  filterPathsFromNav(items, ECONOMIC_IMPACT_PATHS);

const version = packageJson.version;

/**
 * Reads `session_token` from the URL on initial mount
 * strip the param from the URL afterwards */
function PrivateRouteWithSessionToken() {
  const { sessionToken, isAwaitingHandoff } = useSessionTokenHandoff();

  if (isAwaitingHandoff) {
    return (
      <div className={styles.loadingState}>
        <Loading />
      </div>
    );
  }

  return <PrivateRoute sessionToken={sessionToken} />;
}

/**
 * Decides only once Unleash has answered for the logged-in user (see
 * useFlaggedEntityAccess): the flag is user-targeted, so it reads false until
 * then, and redirecting on that early false sent every refresh and shared link
 * to the home page.
 */
function EconomicImpactRoute() {
  const access = useFlaggedEntityAccess(FeatureFlag.EconomicImpactNewLayoutApp);
  if (access === 'pending') return null;
  return access === 'allowed' ? (
    <LazyRoute>
      <EconomicImpactPage />
    </LazyRoute>
  ) : (
    <Navigate to="/" replace />
  );
}

function GlobalAssistantShell({ children }: { children: ReactNode }) {
  const globalAssistantEnabled = useGlobalAIAssistantFeatureEnabled();
  const hideNav = useHideNav();
  if (globalAssistantEnabled) {
    return (
      <GlobalAIAssistantProvider>
        {children}
        {!hideNav && <GlobalAIAssistantHost />}
      </GlobalAIAssistantProvider>
    );
  }
  return children;
}

export function Next() {
  useRecordDocumentArrival();
  useUnleashVersionRefresh();
  useBrandMeta();
  useTopNOverrideFlagGuard();
  const isEconomicImpactEnabled = useUnleashFlag(
    FeatureFlag.EconomicImpactNewLayoutApp
  );

  const hideNav = useHideNav();
  const navItems = (() => {
    let items = allNavItems;
    if (!isEconomicImpactEnabled) {
      items = filterEconomicImpactFromNav(items);
    }
    return items;
  })();
  const routes = reducedRoutes(allNavItems);
  const {
    isOpen: isImpersonationModalOpen,
    onOpen: onImpersonationModalOpen,
    onClose: onImpersonationModalClose,
  } = useDisclosure();
  const handleOpenImpersonationModal = onImpersonationModalOpen;

  const breakpoint = useBreakpoint({ ssr: false });
  if (
    environment.production &&
    (breakpoint === 'sm' || breakpoint === 'base')
  ) {
    return <MobileUnsupportedPage />;
  }

  return (
    <>
      <ImpersonationBanner onEditImpersonation={handleOpenImpersonationModal} />
      {/* Ahead of the routes on purpose: it stamps the navigation during its
          own render, so it has to render before the page it is timing. */}
      <DataInspectorNavigationSync />
      <Routes>
        <Route path={`/${LOGIN}`} element={<Login />} />
        <Route path="/shared/plot/:token" element={<SharedPlotViewer />} />
        <Route path="/shared/:token" element={<SharedReportViewer />} />
        <Route path="/" element={<PrivateRouteWithSessionToken />}>
          <Route
            element={
              <>
                {!hideNav && <Tour />}
                <PageManagerProvider>
                  <GlobalAssistantShell>
                    <SideNav
                      items={navItems}
                      version={version}
                      onOpenImpersonationModal={handleOpenImpersonationModal}
                    />
                  </GlobalAssistantShell>
                </PageManagerProvider>
              </>
            }
          >
            <Route index element={<Homepage />} />

            {routes}

            {/* Gated on the economic-impact-new-layout-app Unleash flag for
                both the sub-nav link (see the navItems filter above) and
                reachability: with the flag off a direct URL redirects to
                landing (EconomicImpactRoute). Auth is already enforced by the
                enclosing PrivateRouteWithSessionToken. Mounted inside the
                layout to keep the dashboard chrome like the other apps. */}
            <Route path="economic-impact" element={<EconomicImpactRoute />} />
          </Route>
          <Route
            path="/gameV2"
            element={
              <RequireAuth adminRequired>
                <LazyRoute>
                  <GameV2 />
                </LazyRoute>
              </RequireAuth>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <ImpersonationModal
        isOpen={isImpersonationModalOpen}
        onClose={onImpersonationModalClose}
      />
      <DataInspectorAccessSync />
      <DataInspectorIndicator />
      <DataInspectorDrawer />
    </>
  );
}
